/* eslint-disable max-statements */
import React, { useState, useEffect } from 'react';
import { getIn } from 'seamless-immutable';
import compose from 'lodash/fp/compose';
import { useQuery } from '@apollo/client';
import { withMetrics } from '@catalogo/core-metrics';
import { withConfig } from '@catalogo/core-connect/config';
import { withCustomer } from '@catalogo/core-connect/customer';
import { getCurrentCategoryId } from '@catalogo/service-americanas-product/src/questions-and-answers/helpers';
import { getCurrentSku } from '@catalogo/core-sku';
import { mergeResults, formatListQuestionsAndAnswers, formatCustomerQuestion } from '../helpers';
import { getQuestionList } from '../../queries/qna.graphql';
import { useTextOfBrand } from '../hook/use-text-of-brand';

const queryParam = {
  term: '',
  limit: 5,
  offset: 0,
  sort: 'likes',
  answersLimit: 1,
  sellerId: 'all',
  condition: 'answered',
  limitOwner: 1,
};

export const formatFetchData = ({
  term,
  limit,
  customer,
  hasChangedOrder,
  fetchMoreResult,
  shouldMergeResults,
  questionsAndAnswers,
}) => {
  const formattedResults = formatListQuestionsAndAnswers({
    data: fetchMoreResult,
    customer,
  });

  if (shouldMergeResults) {
    return mergeResults(questionsAndAnswers, formattedResults);
  }

  if (hasChangedOrder) {
    return {
      questions: [...formattedResults.questions],
      meta: {
        ...formattedResults.meta,
        offset: limit - queryParam.limit,
        limit: queryParam.limit,
      },
    };
  }

  if (term && formattedResults?.questions?.length === 0) {
    const { meta, questions } = formattedResults;
    return {
      ...questionsAndAnswers,
      meta,
      questions,
    };
  }

  return formattedResults;
};
const hasOffers = product => {
  const currentSku = getCurrentSku({ product });
  const sku = getIn(currentSku, ['offers', 'result'], []);
  return sku && sku.length > 0;
};

export const isFeatureEnabled = components => feature =>
  getIn(components, ['page-config', 'features'], '').split(', ').includes(feature);

export const isCrossBorderProduct = product => {
  const currency = getIn(product, ['skus', 0, 'offers', 'result', '0', 'currency']);
  return currency && currency !== 'BRL';
};

const isComponentEnabled = ({ components, breadcrumb, currentSku }) => {
  const uncategorizedEnabled = isFeatureEnabled(components)('QNA_UNCATEGORIZED');
  const rawCategories = getIn(components, ['page-config', 'allowedQnACategories'], '');
  const allowedCategories = rawCategories.split(',');
  const currentCategoryId = getCurrentCategoryId(breadcrumb);
  const hasUsed = getIn(currentSku, ['offers', 'result', 0, 'condition']) === 'USED';

  if (hasUsed) {
    return false;
  }

  if (!currentCategoryId && uncategorizedEnabled) {
    return true;
  }

  return !allowedCategories.includes(currentCategoryId);
};

export const useQnA = (components, product, cache, customer) => {
  const qnaEnabled = isFeatureEnabled(components)('QNA');
  const qnaCrossBorderEnabled = isFeatureEnabled(components)('QNA_CROSSBORDER');
  const isCrossborder = isCrossBorderProduct(product);

  const skip = () => {
    if (!qnaEnabled) {
      return true;
    }

    if (isCrossborder && !qnaCrossBorderEnabled) {
      return true;
    }
    return false;
  };

  const { data, error, fetchMore } = useQuery(getQuestionList, {
    variables: {
      ...queryParam,
      productId: product?.id,
      hasOwnerId: !!customer?.id,
      ownerId: customer?.id,
    },
    skip: skip(),
  });

  return { data, error, fetchMore };
};

const fetchQuestions = async ({
  term = queryParam.term,
  limit = queryParam.limit,
  offset = queryParam.offset,
  sort = queryParam.sort,
  answersLimit = queryParam.answersLimit,
  shouldMergeResults = false,
  hasChangedOrder = false,
  customer,
  fetchMore,
  productId,
  setQuestionsAndAnswers,
  questionsAndAnswers,
}) => {
  queryParam.term = term;
  queryParam.sort = sort;

  await fetchMore({
    variables: { productId, offset, term, sort, limit, answersLimit },
    updateQuery: /* istanbul ignore next */ (_, { fetchMoreResult }) => {
      const result = formatFetchData({
        term,
        limit,
        customer,
        hasChangedOrder,
        fetchMoreResult,
        shouldMergeResults,
        questionsAndAnswers,
      });
      setQuestionsAndAnswers(result);
    },
  });
};

export const withQnA = Component => props => {
  const { config, product, customer, components, breadcrumb, cache, dispatchMetrics, currentSku } = props;
  const [questionsAndAnswers, setQuestionsAndAnswers] = useState(null);
  const [hasQuestion, setHasQuestion] = useState(false);
  const [loading, setLoading] = useState({ button: false, all: false });
  const { data, error, fetchMore } = useQnA(components, product, cache, customer);
  const customerQuestion = getIn(data, ['questionsOwner', 'result', '0'], []);
  const formatedCustomerQuestion = formatCustomerQuestion(customerQuestion);
  const isQnAEnabled = isComponentEnabled({ components, breadcrumb, currentSku });
  const brandText = useTextOfBrand({ brand: config?.brand });
  const productId = product?.id;

  useEffect(() => {
    if (data && !questionsAndAnswers && !error) {
      setQuestionsAndAnswers(formatListQuestionsAndAnswers({ data, customer }));
    }
  }, [data]);

  useEffect(() => {
    if (questionsAndAnswers && !hasQuestion) {
      setHasQuestion(questionsAndAnswers?.questions?.length > 0);
    }
  }, [questionsAndAnswers]);

  const handlerFetchMore = React.useCallback(
    async params => {
      setLoading({ [params.loadingTarget]: true });

      await fetchQuestions({
        ...params,
        customer,
        fetchMore,
        productId,
        setQuestionsAndAnswers,
        questionsAndAnswers,
      });
      setLoading({ [params.loadingTarget]: false });
    },
    [questionsAndAnswers]
  );

  const toQnAMetric = () => {
    dispatchMetrics('event:track', {
      category: 'qna-pdp',
      action: 'link-qna',
    });
  };

  if (isQnAEnabled) {
    return (
      <Component
        hasOffers={hasOffers(product)}
        hasQuestions={hasQuestion}
        questionsAndAnswers={questionsAndAnswers}
        toQnAMetric={toQnAMetric}
        fetchMore={handlerFetchMore}
        loading={loading}
        productId={productId}
        brandText={brandText}
        hasCustomerQuestions={customerQuestion?.answers?.length > 0}
        customerQuestions={formatedCustomerQuestion}
        clientQuestion={customerQuestion}
        {...props}
      />
    );
  }

  return null;
};

export default compose(withCustomer, withConfig, withMetrics, withQnA);
